// src/components/common/FeedbackBanner.js
import React, { useState } from 'react';
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Button,
  Textarea,
  Alert,
  Radio
} from '@material-tailwind/react';
import {
  XMarkIcon,
  CheckCircleIcon,
  PaperAirplaneIcon,
  ExclamationTriangleIcon,
  BugAntIcon,
  LightBulbIcon 
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { submitFeedback } from '../../services/feedback';

const FeedbackBanner = () => {
  const { jeweller } = useAuth();
  const [modalOpen, setModalOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState('bug');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!message.trim()) {
      setError('Please enter a message');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await submitFeedback({
        type: feedbackType,
        title: message,
        description: message,
        userId: jeweller?.id || 'unknown',
        companyName: jeweller?.companyName || 'Unknown Company',
        email: jeweller?.email || 'no-email@provided.com',
        // Mark as quick feedback
        quickFeedback: true
      });

      setSuccess(true);
      setMessage('');
      
      // Close modal after 2 seconds
      setTimeout(() => {
        setModalOpen(false);
        setSuccess(false);
        setFeedbackType('bug'); // Reset to bug
      }, 2000);
    } catch (err) {
      console.error('Error submitting feedback:', err);
      setError(err.message || 'Failed to submit feedback. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setModalOpen(false);
    setMessage('');
    setError(null);
    setSuccess(false);
    setFeedbackType('bug');
  };

  return (
    <>
      {/* Banner - Fixed at bottom */}
      <div style={{
        background: 'var(--Grey-100)',
        borderTop: '1px solid var(--Stroke-Light)',
        padding: 'var(--spacing-xs) var(--spacing-xl)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--spacing-s)',
        flexWrap: 'wrap',
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 40,
        boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.05)'
      }}>
        <span style={{
          fontSize: 'var(--font-size-body5)',
          color: 'var(--Text-Secondary)',
          fontWeight: 'var(--font-weight-medium)',
          textAlign: 'center'
        }}>
          ⚠️ Portal in development. Does something not look right?
        </span>
        <button
          onClick={() => setModalOpen(true)}
          style={{
            background: 'var(--Red-600)',
            color: 'white',
            padding: 'var(--spacing-xxs) var(--spacing-s)',
            borderRadius: 'var(--radius-md)',
            border: 'none',
            fontSize: 'var(--font-size-body5)',
            fontWeight: 'var(--font-weight-semibold)',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'var(--Red-700)';
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'var(--Red-600)';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
          }}
        >
          Report or Suggest
        </button>
      </div>

      {/* Quick Feedback Modal */}
      <Dialog open={modalOpen} handler={handleClose} size="lg">
        <div style={{
          background: 'var(--Surface-Primary)',
          borderRadius: 'var(--radius-xl)',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: 'var(--spacing-l)',
            borderBottom: '1px solid var(--Stroke-Light)'
          }}>
            <h2 style={{
              fontSize: 'var(--font-size-h5)',
              fontWeight: 'var(--font-weight-h5)',
              color: 'var(--Text-Primary)',
              margin: 0
            }}>
              Quick Feedback
            </h2>
            <button 
              onClick={handleClose}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 'var(--spacing-xs)',
                borderRadius: 'var(--radius-s)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'var(--transition-all)'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--Grey-100)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
            >
              <XMarkIcon style={{ width: '1.5rem', height: '1.5rem', color: 'var(--Text-Secondary)' }} />
            </button>
          </div>
          
          <form onSubmit={handleSubmit}>
            {/* Body */}
            <div style={{
              padding: 'var(--spacing-xl)',
              overflowY: 'auto',
              flex: 1,
              maxHeight: 'calc(90vh - 200px)'
            }}>
              {success && (
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 'var(--spacing-s)',
                  padding: 'var(--spacing-m)',
                  borderRadius: 'var(--radius-lg)',
                  marginBottom: 'var(--spacing-l)',
                  background: 'var(--Green-50)',
                  border: '1px solid var(--Green-200)',
                  color: 'var(--Green-800)'
                }}>
                  <CheckCircleIcon style={{ width: '1.5rem', height: '1.5rem', color: 'var(--Green-600)', flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 'var(--font-size-body3)', fontWeight: 'var(--font-weight-semibold)', margin: '0 0 var(--spacing-xxs) 0' }}>
                      Thank you! Your feedback has been submitted successfully.
                    </p>
                  </div>
                </div>
              )}
              
              {error && (
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 'var(--spacing-s)',
                  padding: 'var(--spacing-m)',
                  borderRadius: 'var(--radius-lg)',
                  marginBottom: 'var(--spacing-l)',
                  background: 'var(--Red-50)',
                  border: '1px solid var(--Red-200)',
                  color: 'var(--Red-800)'
                }}>
                  <ExclamationTriangleIcon style={{ width: '1.5rem', height: '1.5rem', color: 'var(--Red-600)', flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 'var(--font-size-body4)', margin: 0 }}>
                      {error}
                    </p>
                  </div>
                </div>
              )}

              {/* Feedback Type Selection */}
              <div style={{ marginBottom: 'var(--spacing-l)' }}>
                <label style={{
                  display: 'block',
                  fontSize: 'var(--font-size-body4)',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--Text-Primary)',
                  marginBottom: 'var(--spacing-s)'
                }}>
                  What would you like to do?
                </label>
                <div style={{ display: 'flex', gap: 'var(--spacing-m)' }}>
                  <button
                    type="button"
                    onClick={() => setFeedbackType('bug')}
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--spacing-s)',
                      padding: 'var(--spacing-m)',
                      background: feedbackType === 'bug' ? 'var(--Red-50)' : 'var(--Surface-Primary)',
                      border: feedbackType === 'bug' ? '2px solid var(--Red-600)' : '2px solid var(--Stroke-Light)',
                      borderRadius: 'var(--radius-lg)',
                      cursor: 'pointer',
                      transition: 'var(--transition-all)',
                      textAlign: 'left'
                    }}
                    onMouseEnter={(e) => feedbackType !== 'bug' && (e.currentTarget.style.borderColor = 'var(--Stroke-Secondary)')}
                    onMouseLeave={(e) => feedbackType !== 'bug' && (e.currentTarget.style.borderColor = 'var(--Stroke-Light)')}
                  >
                    <BugAntIcon style={{ 
                      width: '1.5rem', 
                      height: '1.5rem', 
                      color: feedbackType === 'bug' ? 'var(--Red-600)' : 'var(--Text-Secondary)',
                      flexShrink: 0
                    }} />
                    <div>
                      <div style={{ 
                        fontSize: 'var(--font-size-body3)', 
                        fontWeight: 'var(--font-weight-semibold)',
                        color: 'var(--Text-Primary)',
                        marginBottom: '2px'
                      }}>
                        Report a Bug
                      </div>
                      <div style={{ fontSize: 'var(--font-size-body5)', color: 'var(--Text-Secondary)' }}>
                        Something isn't working
                      </div>
                    </div>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setFeedbackType('suggestion')}
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--spacing-s)',
                      padding: 'var(--spacing-m)',
                      background: feedbackType === 'suggestion' ? 'var(--Purple-50)' : 'var(--Surface-Primary)',
                      border: feedbackType === 'suggestion' ? '2px solid var(--Purple-600)' : '2px solid var(--Stroke-Light)',
                      borderRadius: 'var(--radius-lg)',
                      cursor: 'pointer',
                      transition: 'var(--transition-all)',
                      textAlign: 'left'
                    }}
                    onMouseEnter={(e) => feedbackType !== 'suggestion' && (e.currentTarget.style.borderColor = 'var(--Stroke-Secondary)')}
                    onMouseLeave={(e) => feedbackType !== 'suggestion' && (e.currentTarget.style.borderColor = 'var(--Stroke-Light)')}
                  >
                    <LightBulbIcon style={{ 
                      width: '1.5rem', 
                      height: '1.5rem', 
                      color: feedbackType === 'suggestion' ? 'var(--Purple-600)' : 'var(--Text-Secondary)',
                      flexShrink: 0
                    }} />
                    <div>
                      <div style={{ 
                        fontSize: 'var(--font-size-body3)', 
                        fontWeight: 'var(--font-weight-semibold)',
                        color: 'var(--Text-Primary)',
                        marginBottom: '2px'
                      }}>
                        Make a Suggestion
                      </div>
                      <div style={{ fontSize: 'var(--font-size-body5)', color: 'var(--Text-Secondary)' }}>
                        Idea for improvement
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Message Input */}
              <div style={{ marginBottom: 'var(--spacing-l)' }}>
                <label style={{
                  display: 'block',
                  fontSize: 'var(--font-size-body4)',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--Text-Primary)',
                  marginBottom: 'var(--spacing-xs)'
                }}>
                  {feedbackType === 'bug' ? 'Describe the bug or issue...' : 'Share your suggestion or idea...'}
                  <span style={{ color: 'var(--Red-600)' }}> *</span>
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={6}
                  required
                  placeholder={feedbackType === 'bug' ? 
                    'What happened? Include what you were trying to do and what page you were on.' :
                    'Tell us what you\'d like to see or how we could make things better.'
                  }
                  style={{
                    width: '100%',
                    padding: 'var(--spacing-s)',
                    fontSize: 'var(--font-size-body3)',
                    fontFamily: 'var(--font-family-primary)',
                    color: 'var(--Text-Primary)',
                    background: 'var(--Surface-Primary)',
                    border: '1px solid var(--Stroke-Secondary)',
                    borderRadius: 'var(--radius-s)',
                    transition: 'var(--transition-all)',
                    resize: 'vertical',
                    minHeight: '120px'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'var(--Purple-500)';
                    e.target.style.boxShadow = 'var(--Input-Focus-Ring)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'var(--Stroke-Secondary)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              {/* User Info Display */}
              <div style={{
                background: 'var(--Grey-50)',
                border: '1px solid var(--Stroke-Light)',
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--spacing-m)'
              }}>
                <p style={{
                  fontSize: 'var(--font-size-body5)',
                  color: 'var(--Text-Secondary)',
                  margin: '0 0 var(--spacing-xs) 0',
                  textTransform: 'uppercase',
                  letterSpacing: 'var(--letter-spacing-wide)',
                  fontWeight: 'var(--font-weight-semibold)'
                }}>
                  Submitting as:
                </p>
                <p style={{
                  fontSize: 'var(--font-size-body3)',
                  fontWeight: 'var(--font-weight-medium)',
                  color: 'var(--Text-Primary)',
                  margin: '0 0 2px 0'
                }}>
                  {jeweller?.companyName || 'Unknown'}
                </p>
                <p style={{
                  fontSize: 'var(--font-size-body4)',
                  color: 'var(--Text-Secondary)',
                  margin: 0
                }}>
                  {jeweller?.email || 'No email'}
                </p>
              </div>
            </div>
            
            {/* Footer */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 'var(--spacing-m)',
              padding: 'var(--spacing-l)',
              borderTop: '1px solid var(--Stroke-Light)'
            }}>
              <button
                type="button"
                onClick={handleClose}
                disabled={loading}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 'var(--spacing-xs)',
                  padding: 'var(--spacing-s) var(--spacing-l)',
                  fontSize: 'var(--font-size-button)',
                  fontWeight: 'var(--button-font-weight)',
                  fontFamily: 'var(--font-family-primary)',
                  borderRadius: 'var(--button-radius)',
                  border: 'none',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'var(--transition-all)',
                  boxShadow: 'var(--shadow-sm)',
                  background: 'var(--Button-Secondary-Enabled-BG)',
                  color: 'var(--Button-Secondary-Text)',
                  opacity: loading ? 0.6 : 1
                }}
                onMouseEnter={(e) => !loading && (e.currentTarget.style.background = 'var(--Button-Secondary-Hovered-BG)', e.currentTarget.style.transform = 'translateY(-2px)', e.currentTarget.style.boxShadow = 'var(--shadow-md)')}
                onMouseLeave={(e) => !loading && (e.currentTarget.style.background = 'var(--Button-Secondary-Enabled-BG)', e.currentTarget.style.transform = 'translateY(0)', e.currentTarget.style.boxShadow = 'var(--shadow-sm)')}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !message.trim()}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 'var(--spacing-xs)',
                  padding: 'var(--spacing-s) var(--spacing-xl)',
                  fontSize: 'var(--font-size-button)',
                  fontWeight: 'var(--button-font-weight)',
                  fontFamily: 'var(--font-family-primary)',
                  borderRadius: 'var(--button-radius)',
                  border: 'none',
                  cursor: (loading || !message.trim()) ? 'not-allowed' : 'pointer',
                  transition: 'var(--transition-all)',
                  boxShadow: 'var(--shadow-sm)',
                  background: feedbackType === 'bug' ? 'var(--Red-600)' : 'var(--Button-Gradient-Enabled-BG)',
                  color: 'var(--Text-Contrast)',
                  opacity: (loading || !message.trim()) ? 0.6 : 1
                }}
                onMouseEnter={(e) => !(loading || !message.trim()) && (
                  feedbackType === 'bug' 
                    ? (e.currentTarget.style.background = 'var(--Red-700)', e.currentTarget.style.boxShadow = '0 4px 12px rgba(196, 100, 109, 0.25)')
                    : (e.currentTarget.style.background = 'var(--Button-Gradient-Hovered-BG)', e.currentTarget.style.boxShadow = 'var(--button-shadow-hover)'),
                  e.currentTarget.style.transform = 'translateY(-2px)'
                )}
                onMouseLeave={(e) => !(loading || !message.trim()) && (
                  feedbackType === 'bug'
                    ? (e.currentTarget.style.background = 'var(--Red-600)')
                    : (e.currentTarget.style.background = 'var(--Button-Gradient-Enabled-BG)'),
                  e.currentTarget.style.transform = 'translateY(0)',
                  e.currentTarget.style.boxShadow = 'var(--shadow-sm)'
                )}
              >
                {loading ? (
                  <>
                    <div style={{
                      width: '1.25rem',
                      height: '1.25rem',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      borderTopColor: 'white',
                      borderRadius: '50%',
                      animation: 'spin 0.8s linear infinite'
                    }} />
                    Sending...
                  </>
                ) : (
                  <>
                    <PaperAirplaneIcon style={{ width: '1.25rem', height: '1.25rem' }} />
                    Submit
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </Dialog>
    </>
  );
};

export default FeedbackBanner;